import { format, lastDayOfMonth, parseISO } from "date-fns"

import { OpenDeductionRow } from "@/api/accounting.tsx"

/**
 * The origin period of a held deduction — the month its reclassification should ideally post into
 * when clearing retroactively. The in-store execution date wins; the date it entered the holding
 * account is the fallback.
 */
export function origin_month(row: Pick<OpenDeductionRow, "execution_date" | "posted_date">): string {
  const date = row.execution_date || row.posted_date
  return format(parseISO(date), "yyyy-MM")
}

/** The shared origin month of a selection, or null when the rows span months. */
export function common_origin_month(rows: Pick<OpenDeductionRow, "execution_date" | "posted_date">[]): string | null {
  if (rows.length === 0) return null
  const months = new Set(rows.map(origin_month))
  return months.size === 1 ? [...months][0] : null
}

/** ISO date (yyyy-MM-dd) of the last day of a yyyy-MM month — the retroactive posting date. */
export function month_end_date(iso_month: string): string {
  return format(lastDayOfMonth(parseISO(`${iso_month}-01`)), "yyyy-MM-dd")
}

/** Human label for a yyyy-MM month, e.g. "July 2026". */
export function month_label(iso_month: string): string {
  return format(parseISO(`${iso_month}-01`), "MMMM yyyy")
}

/** Which open rows the page shows. */
export type OpenRowsFilter = "all" | "ready" | "needs_stage" | "needs_coding"

export const OPEN_ROWS_FILTERS: { key: OpenRowsFilter; label: string }[] = [
  { key: "all", label: "All open" },
  { key: "ready", label: "Ready to sync" },
  { key: "needs_stage", label: "Waiting on stage" },
  { key: "needs_coding", label: "Needs coding" },
]

type FilterableRow = Pick<OpenDeductionRow, "ready" | "stage_ready" | "clearing_pending" | "execution_date" | "posted_date" | "cleared_at">

/** The readiness bucket an open row falls in — what the quick filters narrow by. */
export type ReadinessBucket = "ready" | "needs_stage" | "needs_coding" | "pending"

/**
 * Which readiness bucket a row is in. "Waiting on stage" is a `by_stage` row whose deduction
 * hasn't been closed out yet (whatever its coding); "Needs coding" is a row whose stage permits
 * syncing but whose coding is missing or doesn't match the held amount; a row mid-sync is neither.
 */
export function readiness_bucket(row: Pick<OpenDeductionRow, "ready" | "stage_ready" | "clearing_pending">): ReadinessBucket {
  if (row.clearing_pending) return "pending"
  if (row.ready) return "ready"
  if (!row.stage_ready) return "needs_stage"
  return "needs_coding"
}

/** Narrow the open rows by readiness bucket and origin month. */
export function filter_open_rows<T extends FilterableRow>(rows: T[], filter: OpenRowsFilter, month: string | null): T[] {
  return rows.filter((row) => {
    if (month && origin_month(row) !== month) return false
    return filter === "all" || readiness_bucket(row) === filter
  })
}

/** The words the Status column shows for a row — also what its filter and sort go by. */
export type RowStatus = "Cleared" | "Syncing" | "Sync failed" | "Ready" | "Waiting on stage" | "Partly coded" | "Needs coding"

export function row_status(
  row: Pick<OpenDeductionRow, "cleared_at" | "clearing_pending" | "sync_error" | "ready" | "stage_ready" | "partial">
): RowStatus {
  if (row.cleared_at) return "Cleared"
  if (row.clearing_pending) return "Syncing"
  if (row.sync_error) return "Sync failed"
  if (row.ready) return "Ready"
  if (!row.stage_ready) return "Waiting on stage"
  if (row.partial) return "Partly coded"
  return "Needs coding"
}

/**
 * Distinct customers among the rows, A to Z — the customer filter's options. Shared by Open
 * Deductions and the Cash Application check list.
 */
export function distinct_customers(rows: { customer_name?: string | null }[]): string[] {
  return [...new Set(rows.map((row) => row.customer_name).filter((name): name is string => !!name))].sort((a, b) => a.localeCompare(b))
}

/** Distinct values of a text field among the rows, in first-seen order. */
export function distinct_values<T>(rows: T[], get: (row: T) => string | null | undefined): string[] {
  return [...new Set(rows.map(get).filter((value): value is string => !!value))]
}

/** The coded GL accounts of a row as one searchable, sortable string ("" when uncoded). */
export function coding_text(row: Pick<OpenDeductionRow, "splits">): string {
  return row.splits
    .filter((split) => split.account_number)
    .map((split) => `${split.account_number}${split.expense_account ? ` ${split.expense_account}` : ""}`)
    .join(", ")
}

function csv_cell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '""'
  return `"${String(value).replace(/"/g, '""')}"`
}

/** The rows as a CSV (what the table shows, in its order), for the Export CSV button. */
export function open_deductions_csv(rows: OpenDeductionRow[]): string {
  const header = ["Invoice", "Description", "Customer", "Check", "Posted", "Reference date", "Origin", "Category", "Stage", "Coding", "Amount", "Already synced", "Status", "Rebill invoice"]
  const lines = rows.map((row) =>
    [
      row.invoice_number,
      row.description ?? "",
      row.customer_name,
      row.check_number ?? "",
      row.posted_date,
      row.execution_date ?? "",
      month_label(origin_month(row)),
      row.category_name ?? "",
      row.stage,
      coding_text(row),
      Number(row.amount).toFixed(2),
      row.cleared_amount ? Number(row.cleared_amount).toFixed(2) : "",
      row_status(row),
      row.rebill?.invoice_number ?? row.rebill?.invoice_remote_id ?? "",
    ]
      .map(csv_cell)
      .join(",")
  )
  return [header.map(csv_cell).join(","), ...lines].join("\n")
}

/** Distinct origin months of the rows, newest first — the month filter's options. */
export function origin_months(rows: Pick<OpenDeductionRow, "execution_date" | "posted_date">[]): string[] {
  return [...new Set(rows.map(origin_month))].sort((a, b) => (a < b ? 1 : a > b ? -1 : 0))
}

/** Button copy for the batch sync, named after the accounting system the entry posts to. */
export function sync_button_label(accounting_system: string | undefined): string {
  return `Sync to ${accounting_system ?? "accounting"}…`
}

/**
 * Whether a selection can be synced as one batch: non-empty and every row either ready or partly
 * coded (its coded part syncs and the rest stays in the holding account).
 */
export function selection_syncable(rows: Pick<OpenDeductionRow, "ready" | "partial">[]): boolean {
  return rows.length > 0 && rows.every((row) => row.ready || !!row.partial)
}

/** Whether a selection holds a partly coded row — the batch then has to ask for a partial sync. */
export function selection_has_partial(rows: Pick<OpenDeductionRow, "partial">[]): boolean {
  return rows.some((row) => !!row.partial)
}

/**
 * What syncing a partly coded row does, in words: how much moves and how much stays. The amounts
 * arrive as decimal strings, so they are read as numbers before formatting.
 */
export function partial_sync_summary(
  partial: { moving: number; remaining: number },
  format_amount: (amount: number) => string
): string {
  return `${format_amount(Math.abs(Number(partial.moving)))} moves to its coded GL account, ${format_amount(
    Math.abs(Number(partial.remaining))
  )} stays in the holding account`
}

/** Sum row amounts in cents to dodge float drift, returning dollars. */
export function sum_row_amounts(rows: Pick<OpenDeductionRow, "amount">[]): number {
  const cents = rows.reduce((total, row) => total + Math.round(row.amount * 100), 0)
  return cents / 100
}

/**
 * Whether a row can be billed back to the customer: a deduction holding a positive amount that has
 * not been rebilled already. Mirrors the server's `rebill_refusal`, which has the final say.
 */
export function can_rebill(row: Pick<OpenDeductionRow, "kind" | "amount" | "rebill">): boolean {
  return row.kind === "deduction" && Math.round(row.amount * 100) > 0 && !row.rebill
}

/**
 * The GL account a rebill credits unless the user picks another: the deduction's own coding. With
 * several coded accounts it is the one carrying the most dollars; an uncoded deduction has no
 * default and the user has to choose.
 */
export function default_rebill_account_id(row: Pick<OpenDeductionRow, "splits">): string | null {
  let best: { account_id: string; cents: number } | null = null
  for (const split of row.splits) {
    if (!split.account_id) continue
    const cents = Math.abs(Math.round(split.amount * 100))
    if (!best || cents > best.cents) {
      best = { account_id: split.account_id, cents }
    }
  }
  return best?.account_id ?? null
}

/** One reason the accounting system refused a sync, with how many open rows it is holding up. */
export interface FailedSyncReason {
  reason: string
  count: number
}

/**
 * The refusals behind the open rows whose last sync failed, most rows first. Rows refused in one
 * batch share the reason word for word, so they collapse into one line.
 */
export function failed_sync_reasons(rows: Pick<OpenDeductionRow, "sync_error" | "cleared_at">[]): FailedSyncReason[] {
  const counts = new Map<string, number>()
  for (const row of rows) {
    if (row.cleared_at || !row.sync_error) continue
    counts.set(row.sync_error, (counts.get(row.sync_error) ?? 0) + 1)
  }
  return [...counts.entries()]
    .map(([reason, count]: [string, number]) => ({ reason, count }))
    .sort((a: FailedSyncReason, b: FailedSyncReason) => b.count - a.count || a.reason.localeCompare(b.reason))
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  const row = (execution_date: string | null, posted_date: string, amount = 0) => ({
    execution_date,
    posted_date,
    amount,
  })

  describe("origin_month", () => {
    it("prefers the execution date over the holding posted date", () => {
      // Landed in August, originated in July — the Bonadio retroactive-clear case.
      expect(origin_month(row("2026-07-14", "2026-08-27"))).toBe("2026-07")
    })
    it("falls back to the posted date when there is no execution date", () => {
      expect(origin_month(row(null, "2026-08-27"))).toBe("2026-08")
    })
  })

  describe("common_origin_month", () => {
    it("returns the month when every row shares it", () => {
      expect(common_origin_month([row("2026-07-01", "2026-08-27"), row("2026-07-30", "2026-08-01")])).toBe("2026-07")
    })
    it("returns null across months or for an empty selection", () => {
      expect(common_origin_month([row("2026-07-01", "2026-08-27"), row("2026-08-02", "2026-08-27")])).toBeNull()
      expect(common_origin_month([])).toBeNull()
    })
  })

  describe("month_end_date", () => {
    it("lands on the last day, including leap months", () => {
      expect(month_end_date("2026-07")).toBe("2026-07-31")
      expect(month_end_date("2028-02")).toBe("2028-02-29")
    })
  })

  describe("filter_open_rows", () => {
    const open_row = (overrides: Partial<FilterableRow>): FilterableRow => ({
      ready: false,
      stage_ready: true,
      clearing_pending: false,
      execution_date: null,
      posted_date: "2026-09-03",
      cleared_at: null,
      ...overrides,
    })
    const ready = open_row({ ready: true })
    const waiting_on_stage = open_row({ stage_ready: false })
    const needs_coding = open_row({})
    const pending = open_row({ clearing_pending: true })
    const july = open_row({ ready: true, execution_date: "2026-07-14" })
    const rows = [ready, waiting_on_stage, needs_coding, pending, july]

    it("buckets rows by readiness", () => {
      expect(filter_open_rows(rows, "all", null)).toEqual(rows)
      expect(filter_open_rows(rows, "ready", null)).toEqual([ready, july])
      expect(filter_open_rows(rows, "needs_stage", null)).toEqual([waiting_on_stage])
      expect(filter_open_rows(rows, "needs_coding", null)).toEqual([needs_coding])
    })
    it("narrows to an origin month", () => {
      expect(filter_open_rows(rows, "ready", "2026-07")).toEqual([july])
      expect(filter_open_rows(rows, "all", "2026-09")).toEqual([ready, waiting_on_stage, needs_coding, pending])
    })
  })

  describe("row_status / readiness_bucket / coding_text", () => {
    const base = { cleared_at: null, clearing_pending: false, sync_error: null, ready: false, stage_ready: true, partial: null }
    it("names each state the way the badge does, and buckets it the way the quick filters do", () => {
      expect(row_status({ ...base, cleared_at: "2026-10-01T00:00:00Z" })).toBe("Cleared")
      expect(row_status({ ...base, clearing_pending: true })).toBe("Syncing")
      expect(row_status({ ...base, sync_error: "Period is closed." })).toBe("Sync failed")
      expect(row_status({ ...base, ready: true })).toBe("Ready")
      expect(row_status({ ...base, stage_ready: false })).toBe("Waiting on stage")
      expect(row_status({ ...base, partial: { moving: 1, remaining: 2 } })).toBe("Partly coded")
      expect(row_status(base)).toBe("Needs coding")
      expect(readiness_bucket({ ready: true, stage_ready: true, clearing_pending: false })).toBe("ready")
      expect(readiness_bucket({ ready: false, stage_ready: false, clearing_pending: false })).toBe("needs_stage")
      expect(readiness_bucket({ ready: false, stage_ready: true, clearing_pending: false })).toBe("needs_coding")
      expect(readiness_bucket({ ready: true, stage_ready: true, clearing_pending: true })).toBe("pending")
    })
    it("lists the coded accounts and skips uncoded splits", () => {
      const split = (account_number: string | undefined, expense_account: string | undefined) => ({ account_id: null, account_number, expense_account, amount: 1 })
      expect(coding_text({ splits: [split("5850", "Overage / Shortage"), split(undefined, undefined), split("4603", undefined)] })).toBe("5850 Overage / Shortage, 4603")
      expect(coding_text({ splits: [] })).toBe("")
    })
  })

  describe("distinct_customers / open_deductions_csv", () => {
    it("lists customers once, A to Z", () => {
      expect(distinct_customers([{ customer_name: "Walmart" }, { customer_name: "Grocery Supply Company" }, { customer_name: "Walmart" }, { customer_name: "" }])).toEqual([
        "Grocery Supply Company",
        "Walmart",
      ])
    })
    it("writes one quoted line per row and escapes quotes", () => {
      const csv_row = {
        ...row("2026-09-29", "2026-10-14", 4330.48),
        invoice_number: "INV33661",
        description: 'Short pay "POD pending"',
        customer_name: "Grocery Supply Company",
        check_number: "92917",
        category_name: "Operational Issues",
        stage: "new",
        stage_ready: true,
        splits: [{ account_id: "a", account_number: "5850", expense_account: "Overage / Shortage", amount: 4330.48 }],
        rebill: { invoice_remote_id: "8401309", invoice_number: "INV33894", amount: 4330.48, invoice_date: "2026-10-13", created_at: "2026-10-14T00:00:00Z" },
      } as OpenDeductionRow
      const lines = open_deductions_csv([csv_row]).split("\n")
      expect(lines[0]).toBe('"Invoice","Description","Customer","Check","Posted","Reference date","Origin","Category","Stage","Coding","Amount","Already synced","Status","Rebill invoice"')
      expect(lines[1]).toBe(
        '"INV33661","Short pay ""POD pending""","Grocery Supply Company","92917","2026-10-14","2026-09-29","September 2026","Operational Issues","new","5850 Overage / Shortage","4330.48","","Needs coding","INV33894"'
      )
    })
  })

  describe("origin_months", () => {
    it("lists distinct months newest first", () => {
      expect(origin_months([row("2026-07-01", "2026-08-27"), row(null, "2026-09-03"), row("2026-07-20", "2026-09-03")])).toEqual([
        "2026-09",
        "2026-07",
      ])
    })
  })

  describe("sync_button_label / selection_syncable", () => {
    it("names the accounting system and requires every selected row to be ready", () => {
      expect(sync_button_label("NetSuite")).toBe("Sync to NetSuite…")
      expect(sync_button_label(undefined)).toBe("Sync to accounting…")
      expect(selection_syncable([])).toBe(false)
      expect(selection_syncable([{ ready: true }, { ready: false }])).toBe(false)
      expect(selection_syncable([{ ready: true }, { ready: true }])).toBe(true)
    })
    it("lets a partly coded row sync its coded part alongside ready rows", () => {
      const partial = { moving: 1965.88, remaining: 160 }
      expect(selection_syncable([{ ready: true }, { ready: false, partial }])).toBe(true)
      expect(selection_syncable([{ ready: false, partial: null }])).toBe(false)
      expect(selection_has_partial([{ partial: null }, { partial }])).toBe(true)
      expect(selection_has_partial([{}, { partial: null }])).toBe(false)
    })
  })

  describe("partial_sync_summary", () => {
    const dollars = (amount: number) => `$${amount.toFixed(2)}`
    it("says what moves and what stays", () => {
      expect(partial_sync_summary({ moving: 1965.88, remaining: 160 }, dollars)).toBe(
        "$1965.88 moves to its coded GL account, $160.00 stays in the holding account"
      )
    })
    it("reads decimal strings and drops the sign of a repayment", () => {
      const from_api = { moving: "-40.00", remaining: "-60.00" } as unknown as { moving: number; remaining: number }
      expect(partial_sync_summary(from_api, dollars)).toBe("$40.00 moves to its coded GL account, $60.00 stays in the holding account")
    })
  })

  describe("rebill", () => {
    const split = (account_id: string | null, amount: number) => ({ account_id, account_number: "4603", expense_account: "Misc", amount })

    it("defaults to the deduction's own coding", () => {
      expect(default_rebill_account_id({ splits: [split("misc", 62.7)] })).toBe("misc")
    })
    it("picks the account carrying the most dollars when the coding is split", () => {
      expect(default_rebill_account_id({ splits: [split("slotting", 100), split("misc", 400.5), split("display", 250)] })).toBe("misc")
      // Sign does not matter: coding amounts can be stored either way.
      expect(default_rebill_account_id({ splits: [split("slotting", 100), split("misc", -400.5)] })).toBe("misc")
    })
    it("has no default for an uncoded deduction", () => {
      expect(default_rebill_account_id({ splits: [] })).toBeNull()
      expect(default_rebill_account_id({ splits: [split(null, 62.7)] })).toBeNull()
    })
    it("offers a rebill only for a deduction that has not been rebilled", () => {
      expect(can_rebill({ kind: "deduction", amount: 62.7, rebill: null })).toBe(true)
      expect(can_rebill({ kind: "repayment", amount: -62.7, rebill: null })).toBe(false)
      expect(can_rebill({ kind: "deduction", amount: 0, rebill: null })).toBe(false)
      const rebill = { invoice_remote_id: "8355819", invoice_number: "INV33609", amount: 62.7, invoice_date: "2026-09-30", created_at: "2026-09-30T00:00:00Z" }
      expect(can_rebill({ kind: "deduction", amount: 62.7, rebill })).toBe(false)
    })
  })

  describe("sum_row_amounts", () => {
    it("sums in cents so float noise cannot creep in", () => {
      expect(sum_row_amounts([row(null, "2026-08-01", 0.1), row(null, "2026-08-01", 0.2)])).toBe(0.3)
      expect(sum_row_amounts([row(null, "2026-08-01", 250), row(null, "2026-08-01", -24.5)])).toBe(225.5)
    })
  })

  describe("failed_sync_reasons", () => {
    it("groups the open rows a refused batch is holding up by what the accounting system said", () => {
      const missing_customer = "This transaction is missing the Customer dimension for the Account 5100 on line no. 1."
      const rows = [
        { sync_error: missing_customer, cleared_at: null },
        { sync_error: missing_customer, cleared_at: null },
        { sync_error: "Period is closed.", cleared_at: null },
        { sync_error: null, cleared_at: null },
        // A row that has since cleared is history, whatever an earlier attempt said.
        { sync_error: "Period is closed.", cleared_at: "2026-10-13T00:00:00Z" },
      ]
      expect(failed_sync_reasons(rows)).toEqual([
        { reason: missing_customer, count: 2 },
        { reason: "Period is closed.", count: 1 },
      ])
      expect(failed_sync_reasons([{ sync_error: undefined, cleared_at: null }])).toEqual([])
    })
  })
}
